import { Suspense, lazy, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import Home from "../pages/home/HomeNew"; // Eagerly loaded — landing page
import { getAppSettings } from "../api/api";
import { useSelector } from "react-redux";
import { GoogleOAuthProvider } from "@react-oauth/google";

// Lazy-loaded pages (code-split for smaller initial bundle)
const Event = lazy(() => import("../pages/event"));
const PastEvent = lazy(() => import("../pages/event/PastEvent"));
const Search = lazy(() => import("../pages/search"));
const EventDetail = lazy(() => import("../pages/event/EventDetail"));
const RegisterForm = lazy(() => import("../pages/auth/register"));
const Login = lazy(() => import("../pages/auth/login"));
const LoginNew = lazy(() => import("../pages/auth/LoginNew"));
const NotFound = lazy(() => import("./404pagenotfound"));
const ForgotPassword = lazy(() => import("../pages/auth/forgotpassword"));
const ContactUs = lazy(() => import("../pages/contectus"));
const Blog = lazy(() => import("../pages/blog"));
const BlogDetail = lazy(() => import("../pages/blog/blogdetail"));
const Cart = lazy(() => import("../pages/cart"));
const Order = lazy(() => import("../pages/order"));
const Checkout = lazy(() => import("../pages/cart/checkout"));
const Success = lazy(() => import("../pages/cart/success"));
const Cancel = lazy(() => import("../pages/cart/cancel"));
const PrivacyPolicy = lazy(() => import("../pages/privacypolicy"));
const TermsAndCondition = lazy(() => import("../pages/termsandcondition"));
const RefundPolicy = lazy(() => import("../pages/refundpolicy"));
const CancellationPolicy = lazy(() => import("../pages/cancellationpolicy"));
const Orderqr = lazy(() => import("../pages/order/Orderqr"));
const PrivateRoute = ({ children }) => {
  const auth = useSelector((state) => state.auth);

  if (!auth.isAuthenticated) {
    return <Navigate to="/login/booking" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const auth = useSelector((state) => state.auth);

  if (auth.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const MainRoutes = () => {
  const [webname, setWebname] = useState("Dreamland Entertainment");
  const [googleClientId, setGoogleClientId] = useState("");
  const [appSettings, setAppSettings] = useState({});
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const routeTitles = {
      "/": `Home - ${webname}`,
      "/event": `Event - ${webname}`,
      "/past-event": `Past Events - ${webname}`,
      "/blog": `Blog - ${webname}`,
      "/contect-us": `Contect us - ${webname}`,
      "/search": `Search - ${webname}`,
      "/mycart": `My Cart - ${webname}`,
    };

    let title = routeTitles[location.pathname];

    if (!title) {
      if (location.pathname.startsWith("/shop/product")) {
        title = `Product - ${webname}`;
      } else if (location.pathname.startsWith("/blog/detail")) {
        title = `Blog - ${webname}`;
      } else {
        title = webname;
      }
    }

    document.title = title;
  }, [location, webname]);
  const fetchAppSettings = async () => {
    try {
      const response = await getAppSettings();
      setAppSettings(response.data.info);
      setWebname(response.data.info.name);
      setGoogleClientId(response.data.info.google_login_client_id);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchAppSettings();
  }, []);

  return (
    <Suspense fallback={<Spinner animation="border" variant="primary" />}>
      <Routes>
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterForm />
            </PublicRoute>
          }
        ></Route>

        <Route
          path="/login"
          element={
            <PublicRoute>
              {googleClientId ? (
                <GoogleOAuthProvider clientId={googleClientId}>
                  <Login />
                </GoogleOAuthProvider>
              ) : (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
                  <Spinner animation="border" variant="primary" />
                </div>
              )}
            </PublicRoute>
          }
        ></Route>
        {/* NEW DARK LOGIN - for users coming from external booking links */}
        <Route
          path="/login/booking"
          element={
            <PublicRoute>
              {googleClientId ? (
                <GoogleOAuthProvider clientId={googleClientId}>
                  <LoginNew />
                </GoogleOAuthProvider>
              ) : (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
                  <Spinner animation="border" variant="primary" />
                </div>
              )}
            </PublicRoute>
          }
        ></Route>
        <Route
          path="/login/forgotpassword"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        ></Route>

        <Route path="/" element={<Home />}></Route>
        <Route path="/event" element={<Event />}></Route>
        <Route path="/past-event" element={<PastEvent />}></Route>
        <Route path="/event/:id" element={<EventDetail />}></Route>
        <Route path="/search" element={<Search />}></Route>
        <Route path="/contact-us" element={<ContactUs />}></Route>
        <Route path="/blog" element={<Blog />}></Route>
        <Route path="/blog/:id" element={<BlogDetail />}></Route>
        <Route path="/cart" element={<Cart />}></Route>
        <Route path="/checkout" element={<Checkout />}></Route>
        <Route path="/success" element={<Success />}></Route>
        <Route path="/cancel" element={<Cancel />}></Route>
        <Route
          path="/eventhistory"
          element={
            <PrivateRoute>
              <Order />
            </PrivateRoute>
          }
        ></Route>
        <Route path="/orderqr/:id" element={<PrivateRoute><Orderqr /></PrivateRoute>}></Route>
        <Route path="/privacypolicy" element={<PrivacyPolicy />}></Route>
        <Route
          path="/termsandcondition"
          element={<TermsAndCondition />}
        ></Route>
        <Route path="/refundpolicy" element={<RefundPolicy />}></Route>
        <Route path="/cancellationpolicy" element={<CancellationPolicy />}></Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default MainRoutes;
